/* import __COLOCATED_TEMPLATE__ from './sq-input-calendar.hbs'; */
/**
## Input Calendar
This component allows for selecting a single date or a date range, and relies on the
[sq-calendar-month](/components/glass/sq-calendar-month.html) component for rendering the month UI.
It is primarly used within popovers or dropdowns (ex. [sq-field-date](/components/glass/sq-field-date.html),
and [sq-date-filter](/components/dashboard/sq-date-filter.html)).

```example
@title Basic Example
@description Able to select a single date from any past day, including today.
@controller-properties
{
  date: new Date(),
}

@hbs
{{sq-input-calendar mode="SINGLE"
                    selectedDate=(readonly date)}}
```

```example
@title Select Future Date Range
@controller-properties
{
  beginDate: new Date(),
  endDate: moment(new Date()).add(2, 'days')
}

@hbs
{{sq-input-calendar mode="RANGE"
                    beginDate=beginDate
                    endDate=endDate
                    allowPastSelections=false
                    allowPastPageBack=false
                    allowFutureSelections=true
                    allowFuturePageForward=true}}
```

### Parameters
@param {String} [mode="RANGE"] Determines if date range or single date can be selected
@param {Date} beginDate The individual selected day, or start date in selected date range
@param {Date} endDate End date in selected date range
@param {Date} [selectedDate=beginDate] Individual selected date, this is an alias for beginDate
@param {Object} disabledRange Object with beginDate and endDate values. This range of dates won't be able to be selected
@param {Boolean} isToggled Bind anything to isToggled to reset the click counter, e.g. when a calendar popover is dismissed
@param {Boolean} [allowPastPageBack=true] Allows going back to previous months
@param {Boolean} [allowPastSelections=true] Allows selecting past date
@param {Boolean} [allowFuturePageForward=false] Allows going forward to next months
@param {Boolean} [allowFutureSelections=false] Allows selecting future date
 */

import EmberObject, { set, observer, computed } from '@ember/object';
import { on } from '@ember/object/evented';
import { alias } from '@ember/object/computed';
import Component from '@ember/component';
import moment from 'moment';
const MODE = {
  RANGE: 'RANGE',
  SINGLE: 'SINGLE'
};
export const ALL_TIME = {
  ALL_TIME: true
};

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['input-calendar', 'grid-row'],
  classNameBindings: ['_isInvalid', 'inputClassNames'],
  count: 1,
  mode: MODE.RANGE,
  // These need to default to undefined instead of null because they can be passed to moment() and...
  // moment(null) -> invalid date
  // moment(undefined) -> A valid date

  beginDate: undefined,
  endDate: undefined,
  selectedDate: alias('beginDate'),
  disabledRange: null,
  // bind anything to isToggled to reset the click counter, e.g. when a calendar popover is dismissed
  isToggled: null,
  // not a computed property because we use to navigate months
  visibleMonth: null,
  // for setting ranges
  _firstClick: true,
  // for suppressing month navigation on click
  _lockVisibleMonth: false,
  // eslint-disable-next-line ember/no-on-calls-in-components
  beginDateDidChange: on('init',
  // eslint-disable-next-line ember/no-observers
  observer('beginDate', function () {
    // don't jump around when we're clicking
    if (this._lockVisibleMonth) {
      return;
    }
    if (this.get('beginDate') === ALL_TIME) {
      return;
    }
    this.set('visibleMonth', moment(this.get('beginDate')).startOf('month').toDate());
  })),
  // eslint-disable-next-line ember/no-observers
  isToggledDidChange: observer('isToggled', function () {
    this._firstClick = true;
    if (this.get('beginDate') === ALL_TIME) {
      // set to current month if ALL_TIME
      this.set('visibleMonth', moment().startOf('month').toDate());
    } else {
      this.set('visibleMonth', moment(this.get('beginDate')).startOf('month').toDate());
    }
  }),
  months: computed('_months.length', 'canPageBack', 'canPageForward', 'count', 'visibleMonth', function () {
    // we'll cache these for more efficient views and stable DOM elements
    if (!this._months) {
      set(this, '_months', []); // eslint-disable-line ember/no-side-effects
    }
    const {
      count,
      visibleMonth
    } = this.getProperties('count', 'visibleMonth');
    if (!(count && visibleMonth)) {
      return this._months;
    }
    this.set('_months.length', count); // eslint-disable-line ember/no-side-effects

    const calendarWidth = count === 1 ? 'grid-col-24-24' : `grid-col-${Math.floor(24 / count)}-24`;
    for (let monthIndex = 0; monthIndex < count; monthIndex++) {
      if (!this._months[monthIndex]) {
        this._months[monthIndex] = EmberObject.create(); // eslint-disable-line ember/no-side-effects
      }
      const beginDate = moment(visibleMonth).add(monthIndex, 'months').toDate();
      const endDate = moment(beginDate).endOf('month').startOf('day').toDate();

      // eslint-disable-next-line ember/no-side-effects
      this._months[monthIndex].setProperties({
        month: beginDate,
        range: {
          beginDate,
          endDate
        },
        isFirstObject: monthIndex === 0,
        isLastObject: monthIndex === count - 1,
        calendarWidth,
        canPageBack: monthIndex === 0 && this.get('canPageBack'),
        canPageForward: monthIndex === count - 1 && this.get('canPageForward')
      });
    }
    return this._months;
  }),
  _moveVisible(dir) {
    const visibleMonth = moment(this.get('visibleMonth')).add(dir, 'months').toDate();
    this.set('visibleMonth', visibleMonth);
  },
  _withLockedVisibleMonth(cb) {
    this._lockVisibleMonth = true;
    const ret = cb();
    this._lockVisibleMonth = false;
    return ret;
  },
  _selectDate(date) {
    if (moment().isBefore(date) && !this.get('allowFutureSelections')) {
      return false;
    }
    if (!this.get('allowPastSelections') && moment().startOf('day').isAfter(date)) {
      return false;
    }
    const disabledRange = this.get('disabledRange');
    if (disabledRange && disabledRange.beginDate <= date && date <= disabledRange.endDate) {
      return false;
    }
    if (this.get('mode') === MODE.SINGLE) {
      this.set('selectedDate', date);
      if (this.action) {
        this.action(date);
      }
    } else {
      if (this._firstClick) {
        this.setProperties({
          beginDate: date,
          endDate: date
        });
        if (this.action) {
          this.action({
            beginDate: date,
            endDate: date
          });
        }
      } else {
        const times = [this.get('beginDate').getTime(), date.getTime()];
        const begin = Math.min(times[0], times[1]);
        const end = Math.max(times[0], times[1]);
        this.setProperties({
          beginDate: new Date(begin),
          endDate: new Date(end)
        });
        if (this.action) {
          this.action({
            beginDate: new Date(begin),
            endDate: new Date(end)
          });
        }
      }
      this._firstClick = !this._firstClick;
      // TODO: time out clicks so single day selections "stick"? (maybe only on mouseout)
      // TODO: highlight the range that will be selected if clicked?
    }
    return false;
  },
  // bind to this to show/hide the < date nav
  canPageBack: computed('allowPastPageBack', 'visibleMonth', function () {
    return this.get('allowPastPageBack') || moment(this.get('visibleMonth')).isAfter(moment());
  }),
  // bind to this to show/hide the > date nav; by default no paging forward
  canPageForward: computed('allowFuturePageForward', 'visibleMonth', function () {
    return this.get('allowFuturePageForward') || moment(this.get('visibleMonth')).add(1, 'months').isBefore(moment());
  }),
  // bind to this to override default "yes past" config
  allowPastSelections: true,
  allowPastPageBack: true,
  // bind to this to override default "no future" config
  allowFutureSelections: false,
  allowFuturePageForward: false,
  actions: {
    selectRange(range) {
      this._withLockedVisibleMonth(() => {
        if (this.get('mode') === MODE.SINGLE) {
          this._selectDate(range.beginDate);
        } else {
          this.setProperties(range);
          if (this.action) {
            this.action(range);
          }
        }
      });
    },
    selectDate(date) {
      this._withLockedVisibleMonth(() => {
        this._selectDate(date);
      });
    },
    pageBack() {
      if (this.get('canPageBack')) {
        this._moveVisible(-1);
      }
      return false;
    },
    pageForward() {
      if (this.get('canPageForward')) {
        this._moveVisible(1);
      }
      return false;
    }
  }
});